import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Heart, Loader2, Lightbulb, TrendingUp, Shield, PiggyBank,
  DollarSign, Gauge, CreditCard, Target, RefreshCw, Zap,
  ArrowRight, ChevronRight, Save
} from "lucide-react";
import {
  RadialBarChart, RadialBar, ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip
} from "recharts";

const statusConfig = {
  excellent: { color: "text-emerald-400", bg: "bg-emerald-500/10", bar: "#10b981" },
  good: { color: "text-cyan-400", bg: "bg-cyan-500/10", bar: "#06b6d4" },
  fair: { color: "text-amber-400", bg: "bg-amber-500/10", bar: "#f59e0b" },
  poor: { color: "text-red-400", bg: "bg-red-500/10", bar: "#ef4444" },
};

const gradeConfig = {
  A: { color: "text-emerald-400", bg: "bg-emerald-500/10", ring: "#10b981" },
  B: { color: "text-cyan-400", bg: "bg-cyan-500/10", ring: "#06b6d4" },
  C: { color: "text-amber-400", bg: "bg-amber-500/10", ring: "#f59e0b" },
  D: { color: "text-orange-400", bg: "bg-orange-500/10", ring: "#f97316" },
  F: { color: "text-red-400", bg: "bg-red-500/10", ring: "#ef4444" },
};

const compIcons = {
  "Savings Rate": PiggyBank,
  "Debt-to-Income": DollarSign,
  "Budget Adherence": Gauge,
  "Emergency Fund": Shield,
  "Recurring Costs": RefreshCw,
  "Credit Utilization": CreditCard,
  "Goal Progress": Target,
};

const fmt = (v) => parseFloat(v || 0).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 });

export default function HealthScore() {
  const { api } = useAuth();
  const [score, setScore] = useState(null);
  const [history, setHistory] = useState([]);
  const [aiAdvice, setAiAdvice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [scoreRes, histRes] = await Promise.all([
        api.get("/health-score"),
        api.get("/health-score/history"),
      ]);
      setScore(scoreRes.data);
      setHistory(histRes.data);
    } catch {
      toast.error("Failed to load health score");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadData(); }, [loadData]);

  const getAiAdvice = async () => {
    setAiLoading(true);
    try {
      const res = await api.post("/health-score/ai-advice");
      setAiAdvice(res.data);
      toast.success("AI advice generated");
    } catch {
      toast.error("Failed to generate AI advice");
    } finally {
      setAiLoading(false);
    }
  };

  const saveSnapshot = async () => {
    setSaving(true);
    try {
      await api.post("/health-score/snapshot");
      toast.success("Score snapshot saved");
      const histRes = await api.get("/health-score/history");
      setHistory(histRes.data);
    } catch {
      toast.error("Failed to save snapshot");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Health Score">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
        </div>
      </Layout>
    );
  }

  if (!score) {
    return (
      <Layout title="Health Score">
        <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
          <CardContent className="py-16 text-center">
            <Heart className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400 text-sm">Add financial data to calculate your health score</p>
          </CardContent>
        </Card>
      </Layout>
    );
  }

  const gc = gradeConfig[score.grade] || gradeConfig.C;
  const gaugeData = [{ name: "Score", value: score.score, fill: gc.ring }];

  const historyChartData = history.map((h) => ({
    date: new Date(h.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    Score: h.score,
  }));

  return (
    <Layout title="Health Score">
      <div className="space-y-6" data-testid="health-score-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Financial Health Score</h1>
            <p className="text-sm text-zinc-500 mt-1">Your comprehensive financial wellness rating</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={saveSnapshot} disabled={saving} variant="outline" size="sm" className="border-white/10 text-zinc-400 hover:text-white rounded-sm" data-testid="save-snapshot-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
              Save Snapshot
            </Button>
            <Button onClick={getAiAdvice} disabled={aiLoading} variant="outline" size="sm" className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 rounded-sm" data-testid="ai-advice-btn">
              {aiLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Lightbulb className="w-3.5 h-3.5 mr-1.5" />}
              AI Advice
            </Button>
            <Button onClick={loadData} variant="outline" size="sm" className="border-white/10 text-zinc-400 hover:text-white rounded-sm" data-testid="refresh-health-btn">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* Main Score Card */}
        <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="main-score-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-8 flex-wrap">
              {/* Gauge */}
              <div className="w-48 h-48 relative flex-shrink-0" data-testid="score-gauge">
                <ResponsiveContainer width="100%" height="100%">
                  <RadialBarChart cx="50%" cy="50%" innerRadius="70%" outerRadius="100%" startAngle={180} endAngle={0} data={gaugeData}>
                    <RadialBar background={{ fill: "#ffffff08" }} dataKey="value" cornerRadius={4} />
                  </RadialBarChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className={`text-4xl font-bold font-mono ${gc.color}`}>{score.score}</span>
                  <span className="text-xs text-zinc-500">out of 100</span>
                </div>
              </div>

              {/* Grade + Label */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <span className={`text-3xl font-bold px-4 py-1.5 rounded-sm ${gc.bg} ${gc.color}`}>
                    {score.grade}
                  </span>
                  <span className={`text-lg font-semibold ${gc.color}`}>{score.label}</span>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-white/[0.03] p-2.5 rounded-sm">
                    <p className="text-[10px] text-zinc-600 uppercase">Monthly Income</p>
                    <p className="text-sm font-mono text-emerald-400">{fmt(score.snapshot.avg_monthly_income)}</p>
                  </div>
                  <div className="bg-white/[0.03] p-2.5 rounded-sm">
                    <p className="text-[10px] text-zinc-600 uppercase">Monthly Expenses</p>
                    <p className="text-sm font-mono text-red-400">{fmt(score.snapshot.avg_monthly_expense)}</p>
                  </div>
                  <div className="bg-white/[0.03] p-2.5 rounded-sm">
                    <p className="text-[10px] text-zinc-600 uppercase">Debt Payments</p>
                    <p className="text-sm font-mono text-amber-400">{fmt(score.snapshot.monthly_debt_payments)}</p>
                  </div>
                  <div className="bg-white/[0.03] p-2.5 rounded-sm">
                    <p className="text-[10px] text-zinc-600 uppercase">Months Tracked</p>
                    <p className="text-sm font-mono text-white">{score.snapshot.tracked_months}</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Component Scores */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {score.components.map((comp) => {
            const sc = statusConfig[comp.status] || statusConfig.fair;
            const Icon = compIcons[comp.name] || Heart;
            return (
              <Card key={comp.name} className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid={`comp-${comp.name.toLowerCase().replace(/\s/g, "-")}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded flex items-center justify-center flex-shrink-0 ${sc.bg}`}>
                      <Icon className={`w-5 h-5 ${sc.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-white">{comp.name}</span>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-medium px-1.5 py-0.5 rounded-sm ${sc.bg} ${sc.color}`}>{comp.value}</span>
                          <span className="text-sm font-mono text-white font-bold">{comp.score}</span>
                        </div>
                      </div>
                      {/* Progress bar */}
                      <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden mb-1.5">
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${comp.score}%`, backgroundColor: sc.bar }} />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-zinc-600">{comp.tip}</span>
                        <span className="text-[10px] text-zinc-600">Weight: {comp.weight}%</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Score History */}
        {historyChartData.length > 1 && (
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-white flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-cyan-400" /> Score History
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[200px]" data-testid="history-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={historyChartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#71717a" }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#71717a" }} width={35} />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        return (
                          <div className="bg-black/95 border border-white/10 rounded-sm p-3 text-xs">
                            <p className="text-zinc-400 mb-1">{label}</p>
                            <p className="text-white font-mono font-bold">Score: {payload[0].value}</p>
                          </div>
                        );
                      }}
                    />
                    <Line type="monotone" dataKey="Score" stroke="#10b981" strokeWidth={2} dot={{ fill: "#10b981", r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* AI Advice */}
        {aiAdvice && (
          <Card className="bg-[#0A0A0A] border-cyan-500/10 rounded-none" data-testid="ai-advice-section">
            <CardHeader>
              <CardTitle className="text-sm text-white flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-cyan-400" /> AI Financial Advice
                {aiAdvice.projected_score_3mo && (
                  <span className="ml-auto text-xs text-zinc-500 font-normal">
                    Projected: {aiAdvice.projected_score_3mo} (3mo) / {aiAdvice.projected_score_6mo} (6mo)
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {aiAdvice.summary && <p className="text-sm text-zinc-300 leading-relaxed">{aiAdvice.summary}</p>}

              {aiAdvice.top_priority && (
                <div className="bg-emerald-500/5 border border-emerald-500/10 p-3 rounded-sm">
                  <p className="text-xs text-emerald-400 uppercase tracking-wider">Top Priority</p>
                  <p className="text-sm text-white mt-1">{aiAdvice.top_priority}</p>
                </div>
              )}

              {aiAdvice.improvement_plan?.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Improvement Plan</p>
                  <div className="space-y-2">
                    {aiAdvice.improvement_plan.map((item, i) => (
                      <div key={i} className="bg-white/[0.02] border border-white/5 p-3 rounded-sm">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-white font-medium">{item.area}</span>
                          {item.impact_points > 0 && (
                            <span className="text-xs text-emerald-400 font-mono">+{item.impact_points} pts</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-zinc-500 mb-1">
                          <span>{item.current}</span>
                          <ChevronRight className="w-3 h-3" />
                          <span className="text-zinc-300">{item.target}</span>
                        </div>
                        <p className="text-xs text-zinc-400">{item.action}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {aiAdvice.quick_wins?.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Quick Wins</p>
                  <div className="space-y-1.5">
                    {aiAdvice.quick_wins.map((tip, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                        <Zap className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" /> {tip}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {aiAdvice.encouragement && (
                <p className="text-sm text-emerald-300 italic border-l-2 border-emerald-500/30 pl-3">{aiAdvice.encouragement}</p>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
